import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// urlBase64ToUint8Array — required to feed applicationServerKey.
const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const norm = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(norm);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const armed = { current: false };

/**
 * usePushRegistration — registers /sw.js and subscribes the browser to
 * Web Push whenever a user is logged in and the browser has permission.
 * Also wires a `postMessage` bridge from the service worker so clicked
 * notifications navigate inside the SPA instead of causing a hard reload.
 *
 * Safe to call from the top-level App component. Silent no-op on:
 *   - No user logged in
 *   - Browser without Notification / Service Worker / PushManager
 *   - Permission not granted (does NOT prompt — the ConsultationPermissionsGate
 *     and NotificationBell already handle prompting).
 */
export function usePushRegistration({ user }) {
  const navigate = useNavigate();

  // Bridge SW `notificationclick` → SPA navigation
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const onMessage = (e) => {
      if (e.data && e.data.type === 'tababa:push:navigate' && e.data.link) {
        try { navigate(e.data.link); } catch { window.location.href = e.data.link; }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
    if (!VAPID_PUBLIC) return;
    if (armed.current) return;   // guard StrictMode double-invoke
    armed.current = true;

    (async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        // Pull the newest sw.js so devices that installed the app before a
        // ringing fix shipped pick it up on the next launch.
        try { await reg.update(); } catch { /* offline — keep the old one */ }
        // Wait for the SW to be active before subscribing (avoids race).
        await navigator.serviceWorker.ready;
        // Only subscribe if the user already granted permission — otherwise
        // just leave the registration in place and let the user opt-in later.
        if (Notification.permission !== 'granted') return;

        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
          });
        }
        const json = sub.toJSON();
        await axios.post(`${API}/push/subscribe`, {
          endpoint: json.endpoint,
          p256dh:   json.keys?.p256dh,
          auth:     json.keys?.auth,
          user_agent: navigator.userAgent,
        }, { withCredentials: true });
      } catch (e) {
        // Silent — push is best-effort. In-app bell still works.
        console.warn('Push registration failed:', e?.message || e);
        armed.current = false; // allow retry on next mount
      }
    })();
  }, [user]);
}

/**
 * Idempotent — call after the user grants notification permission (from the
 * bell or the ConsultationPermissionsGate) to immediately subscribe without
 * waiting for a re-mount.
 */
export async function ensurePushSubscribed() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  if (Notification.permission !== 'granted') return false;
  if (!VAPID_PUBLIC) return false;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
      });
    }
    const json = sub.toJSON();
    await axios.post(`${API}/push/subscribe`, {
      endpoint: json.endpoint,
      p256dh:   json.keys?.p256dh,
      auth:     json.keys?.auth,
      user_agent: navigator.userAgent,
    }, { withCredentials: true });
    return true;
  } catch {
    return false;
  }
}
